import { useEffect, useRef, useState, useCallback } from "react";
import { PAGE_LENGTH } from "@/utils/constants/constants";

/** Paging, sort, and search passed to the list fetcher on each request. */
export interface FetchListQuery {
  page?: number;
  limit?: number;
  search?: string;
  sort?: string;
  order?: "asc" | "desc";
}

interface FetchListPage<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNextPage: boolean;
  };
}

const DEFAULT_FILTERS: Record<string, any> = {};

/**
 * Server-backed list: infinite append loading, search, and normalized filters for the fetcher.
 */
export function useFetchListApi<
  T,
  F extends Record<string, any> = Record<string, any>
>(
  fetcher: (options: FetchListQuery & { filters?: F }) => Promise<FetchListPage<T>>,
  initial: FetchListQuery = { page: 1, limit: PAGE_LENGTH, sort: "updated_at", order: "desc" },
  initialFilters: F = DEFAULT_FILTERS as F
) {
  const initialRef = useRef(initial);
  initialRef.current = initial;
  const initialFiltersRef = useRef(initialFilters);
  initialFiltersRef.current = initialFilters;

  const [options, setOptions] = useState<FetchListQuery>(initial);
  const [filters, setFilters] = useState<F>(initialFilters);

  const [items, setItems] = useState<T[]>([]);
  const [pagination, setPagination] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasNextPage, setHasNextPage] = useState(true);

  const observerRef = useRef<HTMLDivElement | null>(null);
  const fetchInFlightRef = useRef(false);
  const optionsRef = useRef(options);
  const filtersRef = useRef(filters);
  const hasNextRef = useRef(hasNextPage);
  const itemsRef = useRef(items);

  optionsRef.current = options;
  filtersRef.current = filters;
  hasNextRef.current = hasNextPage;
  itemsRef.current = items;

  const loadMore = useCallback(async () => {
    if (fetchInFlightRef.current || !hasNextRef.current) return;
    fetchInFlightRef.current = true;

    const append = itemsRef.current.length > 0;
    if (append) setLoadingMore(true);
    else setLoading(true);

    try {
      const result = await fetcher({
        ...optionsRef.current,
        filters: filtersRef.current,
      });

      setItems((prev) => [...prev, ...result.data]);
      setPagination(result.pagination);
      const nextHasNext =
        result.pagination.hasNextPage && result.data.length > 0;
      hasNextRef.current = nextHasNext;
      setHasNextPage(nextHasNext);

      setOptions((prev) => ({
        ...prev,
        page: (prev.page || 1) + 1,
      }));
    } catch {
      hasNextRef.current = false;
      setHasNextPage(false);
    } finally {
      fetchInFlightRef.current = false;
      setLoading(false);
      setLoadingMore(false);
    }
  }, [fetcher]);

  useEffect(() => {
    if (items.length > 0 || !hasNextPage || loading || loadingMore) return;
    loadMore();
  }, [items.length, hasNextPage, loading, loadingMore, loadMore]);

  useEffect(() => {
    if (items.length === 0 || !hasNextPage || !observerRef.current) return;

    const node = observerRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          !fetchInFlightRef.current &&
          hasNextRef.current
        ) {
          loadMore();
        }
      },
      { threshold: 1.0 }
    );

    observer.observe(node);

    return () => observer.disconnect();
  }, [loadMore, hasNextPage, items.length]);

  const handleSearch = (search: string) => {
    setItems([]);
    setOptions({ ...initialRef.current, search });
    setHasNextPage(true);
  };

  const handleFilters = (newFilters: Partial<F>) => {
    setItems([]);
    if (Object.keys(newFilters).length > 0) {
      // Replace, do not merge into prev — omitted keys (e.g. cleared size) must drop minSize/maxSize from API state
      setFilters(normalizeFilters(newFilters) as F);
      setOptions({ ...initialRef.current });
    } else {
      setFilters(initialFiltersRef.current);
      setOptions(initialRef.current);
    }
    setHasNextPage(true);
  };

  const reset = useCallback(() => {
    setItems([]);
    setFilters(initialFiltersRef.current);
    setOptions({ ...initialRef.current });
    setHasNextPage(true);
    hasNextRef.current = true;
  }, []);

  const refetch = useCallback(() => {
    setItems([]);
    setOptions((prev) => ({
      ...initialRef.current,
      page: 1,
      search: prev.search,
      sort: prev.sort,
      order: prev.order,
      limit: prev.limit ?? initialRef.current.limit,
    }));
    setHasNextPage(true);
  }, []);

  return {
    items,
    loading,
    loadingMore,
    pagination,
    hasNextPage,
    observerRef,
    handleSearch,
    filters,
    handleFilters,
    reset,
    refetch,
    search: options.search ?? "",
  };
}

function normalizeFilters(raw: any = {}) {
  const filter: Record<string, any> = {};

  if (raw.type?.value) {
    filter.fileType = raw.type.value;
  }
  if (raw.dateRange) {
    const sd = raw.dateRange.startDate ?? null;
    const ed = raw.dateRange.endDate ?? null;
    if (sd != null) filter.startDate = sd;
    if (ed != null) filter.endDate = ed;
  }
  if (raw.status?.value != null && raw.status?.value !== "") {
    filter.status = raw.status.value;
  }
  const ownerVal = raw.owner?.value;
  if (ownerVal && ownerVal !== "anyone") {
    filter.owner = ownerVal;
  }
  if (raw.tags) {
    filter.tags = raw.tags ?? [];
  }
  const sr = raw.sizeRange;
  if (
    sr &&
    ((sr.min !== "" && sr.min != null) || (sr.max !== "" && sr.max != null))
  ) {
    const unit = raw.sizeUnit || "KB";
    const mult =
      unit === "GB" ? 1024 * 1024 * 1024 : unit === "MB" ? 1024 * 1024 : 1024;
    if (sr.min !== "" && sr.min != null) filter.minSize = Number(sr.min) * mult;
    if (sr.max !== "" && sr.max != null) filter.maxSize = Number(sr.max) * mult;
  }
  const shared_to =
    typeof raw.shared_to === "string" ? raw.shared_to.trim() : "";
  if (shared_to) {
    filter.shared_to = shared_to;
  }
  return filter;
}
